import React from "react";

import CheckRounded from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import { Button, Stack, Typography, Menu, MenuItem } from "@mui/material";

import { RollupFilterOption } from "@app/analytics/providers/RollupContext";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import RollupFilterIcon from "./RollupFilterIcon";

interface FilterDropdownProps {
  id: string;
  options: RollupFilterOption[];
  selected: RollupFilterOption;
  onChange: (newValue: RollupFilterOption) => void;
  icon: React.FunctionComponent;
  disabled?: boolean;
}

const FilterDropdown = ({
  id,
  options,
  selected,
  onChange,
  icon,
  disabled,
}: FilterDropdownProps) => {
  const { anchorElement, open, onClick, onClose, width } = useAnchorElement();

  const handleChange = (newOption: RollupFilterOption) => {
    onChange(newOption);
    onClose();
  };

  const disableDropdown = disabled || options.length <= 1;

  return (
    <>
      <Button
        disabled={disableDropdown}
        aria-controls={open ? id : undefined}
        aria-haspopup
        aria-expanded={open ? "true" : undefined}
        onClick={onClick}
        sx={{
          bgcolor: disabled ? "shades.netural.50" : "white",
          minWidth: "220px",
          border: "1px solid",
          borderRadius: "8px",
          borderColor: "shades.neutral.100",
          color: "shades.neutral.500",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="start">
            <RollupFilterIcon
              icon={icon}
              sx={{
                bgcolor: disabled ? "shades.primary.300" : "shades.primary.500",
              }}
            />
            <Typography
              variant="bodySecondaryReg"
              sx={{ color: "shades.neutral.600" }}
            >
              {selected.label}
            </Typography>
          </Stack>
          {!disableDropdown && (
            <KeyboardArrowDownRounded
              sx={{ width: "20px", ml: 1, color: "#323232" }}
            />
          )}
        </Stack>
      </Button>
      <Menu
        id="reports-pagination-menu"
        anchorEl={anchorElement}
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiPaper-root": {
            width: width,
            boxShadow: (theme) => theme.shadows[4],
          },
        }}
      >
        {options.map((opt) => {
          const isSelected = opt.id === selected.id;
          return (
            <MenuItem
              key={opt.id}
              onClick={() => handleChange(opt)}
              sx={{ minWidth: "80px" }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Typography
                  variant="bodySecondaryReg"
                  sx={{
                    color: isSelected
                      ? "shades.neutral.800"
                      : "shades.neutral.500",
                  }}
                >
                  {opt.label}
                </Typography>
                {isSelected && (
                  <CheckRounded sx={{ height: "18px", width: "18px" }} />
                )}
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default FilterDropdown;
